import { Accordion, AccordionItem } from '@heroui/react';
import type { ReactNode } from 'react';

import { LogCard, nestedAccordionItemClasses } from './shared';
import type { LogCardAccordionItemClasses } from './shared/accordionStyles';
import { AgentToolCallConfig } from '../utils/logParser';

import i18n from '~/common/helpers/i18n';
import { Code } from '~/components/Code';

interface ToolCallCardProps {
  config: AgentToolCallConfig;
  annotationSlot?: ReactNode;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  itemKey?: string;
  itemClasses?: LogCardAccordionItemClasses;
  className?: string;
  nestedAccordionSyncKey?: number;
  nestedAccordionSyncExpanded?: boolean;
}

/**
 * Component to display agent.tool_call in a structured card with collapsible Arguments and Result sections
 */
/** Use JSON language only when output looks like JSON; otherwise plain text (e.g. fetch response). */
function getOutputLanguage(output: string): 'json' | 'text' {
  const trimmed = output.trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[') ? 'json' : 'text';
}

export function ToolCallCard({
  config,
  annotationSlot,
  isExpanded,
  onExpandedChange,
  itemKey,
  itemClasses,
  className,
  nestedAccordionSyncKey,
  nestedAccordionSyncExpanded
}: ToolCallCardProps) {
  const title = (
    <span className='text-sm font-semibold text-on-surface'>
      {i18n.t('scenes.build.execution.logs.tool_call.label') as string}
    </span>
  );

  const nestedAccordionRemountKey =
    nestedAccordionSyncKey !== undefined
      ? `tool-call-nested-${nestedAccordionSyncKey}`
      : undefined;
  let nestedAccordionDefaultKeys: string[] = [];
  if (nestedAccordionSyncKey !== undefined) {
    nestedAccordionDefaultKeys = nestedAccordionSyncExpanded
      ? ['arguments', 'result']
      : [];
  }

  return (
    <LogCard
      title={title}
      textValue={
        i18n.t('scenes.build.execution.logs.tool_call.label') as string
      }
      footer={annotationSlot}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      itemKey={itemKey}
      itemClasses={itemClasses}
      className={className}
    >
      {/* Tool name display */}
      <div className='mb-4'>
        <span className='text-sm font-medium text-on-surface'>
          {i18n.t('scenes.build.execution.logs.tool_call.tool') as string}
          {': '}
          <span className='text-primary'>{config.toolName}</span>
        </span>
      </div>

      {/* Arguments and Result sections */}
      <Accordion
        key={nestedAccordionRemountKey}
        selectionMode='multiple'
        defaultSelectedKeys={nestedAccordionDefaultKeys}
        itemClasses={nestedAccordionItemClasses}
        showDivider={false}
      >
        {/* Arguments section */}
        <AccordionItem
          key='arguments'
          title={
            <span className='text-xs font-medium text-on-surface'>
              {
                i18n.t(
                  'scenes.build.execution.logs.tool_call.arguments'
                ) as string
              }
            </span>
          }
          textValue={
            i18n.t('scenes.build.execution.logs.tool_call.arguments') as string
          }
        >
          <div className='max-w-full overflow-x-auto rounded-md border border-outline'>
            <Code
              value={config.params}
              language='json'
              readOnly
              height='200px'
              disableJsonValidation={false}
              classNames={{
                wrapper: 'rounded'
              }}
            />
          </div>
        </AccordionItem>

        {/* Result section */}
        <AccordionItem
          key='result'
          title={
            <span className='text-xs font-medium text-on-surface'>
              {i18n.t('scenes.build.execution.logs.tool_call.result') as string}
            </span>
          }
          textValue={
            i18n.t('scenes.build.execution.logs.tool_call.result') as string
          }
        >
          <div className='max-w-full overflow-x-auto rounded-md border border-outline'>
            <Code
              value={config.output}
              language={getOutputLanguage(config.output)}
              readOnly
              height='200px'
              disableJsonValidation={
                getOutputLanguage(config.output) !== 'json'
              }
              classNames={{
                wrapper: 'rounded'
              }}
            />
          </div>
        </AccordionItem>
      </Accordion>
    </LogCard>
  );
}
